import { Injectable, OnDestroy } from '@angular/core';
import { DynamicTableColDef } from '@frk/eds-components';
import { PortfolioHolding } from '@models';
import {
  EMPTY_ROW,
  ExcelDownloadService,
  FtCellObject,
  getCellObject,
  getColWidths,
} from '@products/services/excel-download.service';
import { TranslateService } from '@shared/translate/translate.service';
import { Logger } from '@utils/logger';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExcelDataType, WorkBook, WorkSheet, XLSX$Utils } from 'xlsx';

const logger = Logger.getLogger('PortfolioDownloadService');

@Injectable({
  providedIn: 'root',
})
export class PortfolioDownloadService implements OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private excelService: ExcelDownloadService,
    private translateService: TranslateService
  ) {}

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public downloadHoldings(
    fundName: string,
    asOfDate: string,
    colDefs: DynamicTableColDef<PortfolioHolding>[],
    rowData: PortfolioHolding[]
  ): void {
    logger.debug('downloadHoldings', asOfDate, colDefs, rowData);

    // Load Caveats
    forkJoin([
      this.excelService.getProximalCaveat$('FullHoldingsExcelTop'),
      this.excelService.getProximalCaveat$('FullHoldingsExcelBottom'),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([topCaveat, bottomCaveat]) => {
        this.createXlsx(
          fundName,
          asOfDate,
          colDefs,
          rowData,
          topCaveat,
          bottomCaveat
        );
      });
  }

  private createXlsx = (
    fundName: string,
    asOfDate: string,
    colDefs: DynamicTableColDef<PortfolioHolding>[],
    rowData: PortfolioHolding[],
    topCaveat: FtCellObject[][],
    bottomCaveat: FtCellObject[][]
  ): Promise<void> => {
    // Import SheetJS
    return import('xlsx').then((xlsx) => {
      const portfolioHoldingTabname = this.translateService.instant(
        'products.portfolio-allholding-tabname'
      );
      const portfolioHoldingFilename = this.translateService.instant(
        'products.portfolio-allholding-filename'
      );

      const xlsxUtils: XLSX$Utils = xlsx.utils;

      // Create new work book
      const workbook: WorkBook = xlsx.utils.book_new();

      // assemble sheet
      const worksheet: WorkSheet = this.assembleWorkSheet(
        xlsxUtils,
        colDefs,
        rowData,
        topCaveat,
        bottomCaveat,
        fundName,
        asOfDate
      );

      // add worksheet to workbook
      xlsxUtils.book_append_sheet(workbook, worksheet, portfolioHoldingTabname);

      // write workbook
      xlsx.writeFile(workbook, portfolioHoldingFilename);
    });
  };

  // assemble sheet config and data into data to generate xlsx
  private assembleWorkSheet = (
    xlsxUtils: XLSX$Utils,
    colDefs: DynamicTableColDef<PortfolioHolding>[],
    rowData: PortfolioHolding[],
    topCaveat: FtCellObject[][],
    bottomCaveat: FtCellObject[][],
    fundName: string,
    asOfDate: string
  ): WorkSheet => {
    // get cells for top caveat and as of date
    const fundNameRow: FtCellObject[][] = [
      [
        getCellObject(fundName, 's'),
        getCellObject(this.translateService.instant('products.as-of'), 's'),
        getCellObject(asOfDate, 's'),
      ],
    ];

    // get cells for row headers
    const headers: FtCellObject[][] = [colDefs.map(this.getHeader)];

    // get cells for data rows
    const rows: FtCellObject[][] = rowData.map(
      (row: PortfolioHolding): FtCellObject[] => colDefs.map(this.mapCol(row))
    );

    // combine data rows
    // These rows are what column widths will be based on
    const dataCells: FtCellObject[][] = fundNameRow
      .concat(EMPTY_ROW)
      .concat(headers)
      .concat(rows);

    // assemble into 2D array of cells and return
    const sheetCells: FtCellObject[][] = topCaveat
      .concat(EMPTY_ROW)
      .concat(dataCells)
      .concat(EMPTY_ROW, EMPTY_ROW, EMPTY_ROW, EMPTY_ROW, EMPTY_ROW)
      .concat(bottomCaveat);

    logger.debug('sheet cells', sheetCells);

    const workSheet: WorkSheet = xlsxUtils.aoa_to_sheet(sheetCells);

    // calc col widths
    workSheet['!cols'] = getColWidths(dataCells);

    return workSheet;
  };

  /**
   * Get header row(s) from col defs
   */
  private getHeader = (
    colDef: DynamicTableColDef<PortfolioHolding>
  ): FtCellObject => {
    return getCellObject(
      colDef.headerName,
      's',
      null,
      null,
      colDef.tooltipContent
    );
  };

  // Get cell for combination of colDef and fund row
  private mapCol = (row: PortfolioHolding) => (
    colDef: DynamicTableColDef<PortfolioHolding>
  ): FtCellObject => {
    // work out cell value from function or field name
    const val = colDef.valueGetter
      ? colDef.valueGetter({ node: row, colDef })
      : row[colDef.field];

    // work out data type
    let dataType: ExcelDataType; // default to string
    switch (colDef.dataType) {
      case 'currency':
        dataType = 'n';
        break;
      case 'date':
        dataType = 'd';
        break;
      case 'number':
        dataType = 'n';
        break;
      case 'percent':
        dataType = 'n';
        break;
      default:
        dataType = 's';
    }

    return getCellObject(val, dataType);
  };
}
